import { matchRoutes } from 'react-router-dom';

import { routeSuperset } from '../../config/routes';

// Routes that must never produce an analytics event, matched on the
// reconstructed pattern. `/advisor/clients` is the employee client-select
// screen — the web equivalent of mobile's EmployeeClientSelect blacklist entry.
const BLACKLIST = ['/advisor/clients'];

/**
 * matchRoutes needs the same tree shape the real router matches — a child on
 * its parent's path becomes an index route — but routeTree.js imports App, so
 * using it here would be an import cycle (App → tracking → routeTree → App).
 * Build the equivalent tree from the superset, minus the elements matching
 * never looks at.
 */
const toMatchNode = (route, parentPath = null) => {
  const node = {};
  if (route.public) {
    node.public = true;
  }
  if (route.path === parentPath) {
    node.index = true;
  } else {
    node.path = route.path;
  }
  if (route.routes?.length) {
    node.children = route.routes.map((child) => toMatchNode(child, route.path));
  }
  return node;
};

const matchTree = routeSuperset.map((route) => toMatchNode(route));

/**
 * Resolve a concrete pathname to `{ pattern, resourceId, params }`.
 *
 * The pattern is the matched route's path string (`/todo/:type/:id`), never
 * the concrete pathname and never a query string — this module owns that
 * guarantee. Child paths are absolute in the route table, so the pattern is
 * the deepest matched route that has a path; an index match's pattern is its
 * parent's. The top-level catchall matches as `*` — tracked under that name,
 * since 404 hits are useful signal.
 *
 * `:id` params become `resourceId`; every other param is returned in `params`
 * as strings. Returns null for public, blacklisted or unmatched pathnames.
 */
export const resolveRoutePattern = (pathname) => {
  const matches = matchRoutes(matchTree, { pathname });
  if (!matches) return null;

  let pattern = null;
  let params = {};
  let isPublic = false;
  matches.forEach((match) => {
    if (match.route.path) {
      pattern = match.route.path;
    }
    if (match.route.public) {
      isPublic = true;
    }
    params = match.params;
  });

  // Public routes (login, registration, the auth callbacks) are pre-auth and
  // never tracked, including a mid-session re-auth pass through them.
  if (!pattern || isPublic || BLACKLIST.includes(pattern)) return null;

  const { id, '*': splat, ...rest } = params;
  const stringParams = {};
  Object.entries(rest).forEach(([key, value]) => {
    if (value != null) {
      stringParams[key] = String(value);
    }
  });

  return {
    pattern,
    resourceId: id,
    params: Object.keys(stringParams).length ? stringParams : undefined,
  };
};
